import type Phaser from 'phaser';
import { gameAudio } from '../audio/audio';
import { devAreaOpen, REG_MENU_MODE } from '../devAccess';
import { ONLINE_ENABLED } from '../../platform/online';
import { inboxNews, onHub } from '../hub';
import { challengeNews, onChallengeNews, onlineOn } from '../online';
import { saves } from '../records';
import { unseenUnlocks } from '../../meta/reveals';
import { label, UI } from './text';
import { drawCoin } from './widgets';

/**
 * The main screens' frame (owner, 2026-10-05: one navigation instead of tabs on top and buttons at
 * the bottom): a slim header with the screen's name, the coin counter and a settings gear, and a
 * bottom bar of five tabs in thumb reach. LAWNS, DAILY and RUNS are the menu's three faces; GARAGE
 * and CLUB (TROPHY in builds without online play, docs/ONLINE_UI.md) are their own scenes. Switching tabs is instant (the mowing wipe is for going into
 * and out of lawns).
 */

export type NavTab = 'lawns' | 'daily' | 'runs' | 'garage' | 'club';

/** Header band (screen name, coins, gear) and the bottom tab bar. */
export const HEADER_H = 18;
export const TABBAR_H = 30;

const TABS: { tab: NavTab; name: string }[] = [
  { tab: 'lawns', name: 'LAWNS' },
  { tab: 'daily', name: 'DAILY' },
  { tab: 'runs', name: 'RUNS' },
  { tab: 'garage', name: 'GARAGE' },
  { tab: 'club', name: ONLINE_ENABLED ? 'CLUB' : 'TROPHY' },
];

/** The menu face each of its tabs opens (the registry key Menu reads). */
const MENU_MODE: Partial<Record<NavTab, string>> = {
  lawns: 'campaign',
  daily: 'daily',
  runs: 'endless',
};

/** Where Settings returns to: the tab it was opened from. */
export const REG_SETTINGS_FROM = 'settings.from';

/**
 * Set when LAWNS is tapped while already on it (owner, 2026-10-07): the menu goes back to the zone
 * of the next lawn to play, its lawn selected.
 */
export const REG_MENU_HOME = 'menu.home';

/** Opens a tab: one of the menu's faces, the Garage, or CLUB (on YOU, the trophy shelf). */
export function openTab(scene: Phaser.Scene, tab: NavTab): void {
  const mode = MENU_MODE[tab];
  if (mode) {
    scene.registry.set(REG_MENU_MODE, mode);
    scene.scene.start('Menu');
  } else {
    scene.scene.start(tab === 'garage' ? 'Garage' : 'Trophy');
  }
}

export interface Header {
  /** The coin counter (right-aligned): purchases count it down, rewards fly into it. */
  wallet: Phaser.GameObjects.BitmapText;
  /** The coin icon's center, where flying coins land. */
  coin: { x: number; y: number };
}

/**
 * The header: the screen's name, the coin counter and the settings gear (and, when the DEV area
 * is open, a DEV button that opens the hand-built test lawns).
 */
export function drawHeader(scene: Phaser.Scene, title: string, from: NavTab): Header {
  const { width } = scene.scale.gameSize;
  const g = scene.add.graphics();
  g.fillStyle(UI.panel).fillRect(0, 0, width, HEADER_H);
  g.fillStyle(UI.rule).fillRect(0, HEADER_H, width, 1);
  g.fillStyle(UI.shadow).fillRect(0, HEADER_H + 1, width, 1);
  label(scene, 6, 6, title, 1, UI.accent);

  // Gear at the right edge, a thumb-sized target.
  const gx = width - 14;
  drawGear(g, gx - 4, 5);
  scene.add
    .zone(gx - 10, 0, 24, HEADER_H)
    .setOrigin(0)
    .setData('label', 'SETTINGS')
    .setInteractive({ useHandCursor: true })
    .on('pointerup', () => {
      gameAudio.play('click');
      scene.registry.set(REG_SETTINGS_FROM, from);
      scene.scene.start('Settings');
    });

  // Coins, left of the gear.
  const wallet = label(scene, gx - 10, 6, String(saves().save.coins), 1, UI.accent).setOrigin(1, 0);
  const cx = Math.round(wallet.x - wallet.width - 10);
  drawCoin(g, cx, 6);

  // CLUB's inbox (docs/FRIENDS.md): an envelope left of the coins, a dot while invites wait.
  if (from === 'club' && ONLINE_ENABLED && onlineOn()) {
    const ex = cx - 22;
    drawEnvelope(g, ex, 6, inboxNews());
    scene.add
      .zone(ex - 5, 0, 22, HEADER_H)
      .setOrigin(0)
      .setData('label', 'INBOX')
      .setInteractive({ useHandCursor: true })
      .on('pointerup', () => {
        gameAudio.play('click');
        scene.scene.start('Inbox');
      });
  }

  if (devAreaOpen() && from !== 'garage' && from !== 'club') {
    const dev = label(scene, cx - 8, 6, 'DEV', 1, UI.dim).setOrigin(1, 0);
    scene.add
      .zone(dev.x - dev.width - 4, 0, dev.width + 8, HEADER_H)
      .setOrigin(0)
      .setData('label', 'DEV')
      .setInteractive({ useHandCursor: true })
      .on('pointerup', () => {
        gameAudio.play('click');
        scene.registry.set(REG_MENU_MODE, 'test');
        scene.scene.start('Menu');
      });
  }
  return { wallet, coin: { x: cx + 3, y: 9 } };
}

/**
 * The bottom tab bar: an icon over a name for each tab, the current one raised and yellow. The
 * GARAGE tab gets a pulsing dot while something unlocked there has not been looked at, and CLUB
 * while a friend's answer to a challenge is unseen (also when it arrives with the bar up).
 */
export function drawTabBar(scene: Phaser.Scene, active: NavTab): void {
  const badge: NavTab[] = [];
  if (unseenUnlocks(saves().save).length > 0) badge.push('garage');
  if (challengeNews() || inboxNews()) badge.push('club');
  const { width, height } = scene.scale.gameSize;
  const top = height - TABBAR_H;
  const g = scene.add.graphics();
  g.fillStyle(UI.shadow).fillRect(0, top - 1, width, 1);
  g.fillStyle(UI.panel).fillRect(0, top, width, TABBAR_H);
  g.fillStyle(UI.rule).fillRect(0, top, width, 1);
  const w = width / TABS.length;
  TABS.forEach(({ tab, name }, i) => {
    const x = Math.round(i * w);
    const cw = Math.round((i + 1) * w) - x;
    const on = tab === active;
    if (on) {
      g.fillStyle(UI.panelHi).fillRect(x + 1, top + 2, cw - 2, TABBAR_H - 3);
      g.fillStyle(UI.accent).fillRect(x + 1, top + 2, cw - 2, 1);
    }
    const color = on ? UI.accent : UI.dim;
    drawTabIcon(g, tab, x + Math.round(cw / 2) - 5, top + 5, color);
    label(scene, x + Math.round(cw / 2), top + 18, name, 1, on ? UI.text : UI.dim).setOrigin(
      0.5,
      0,
    );
    const dotAt = () => drawDot(scene, x + Math.round(cw / 2) + 7, top + 5, `${tab}-dot`);
    if (badge.includes(tab) && !on) dotAt();
    const zone = scene.add
      .zone(x, top, cw, TABBAR_H)
      .setOrigin(0)
      .setData('label', name)
      .setInteractive({ useHandCursor: true })
      .on('pointerup', () => {
        gameAudio.play('click');
        if (on && tab === 'lawns') scene.registry.set(REG_MENU_HOME, true);
        openTab(scene, tab);
      });
    if (tab === 'club' && !on && !badge.includes('club')) {
      // News that arrives while this bar is up (the bar is redrawn often: once is enough).
      const stopNews = onChallengeNews(() => {
        stop();
        if (zone.scene) dotAt();
      });
      // An invite arriving does the same.
      const stopHub = onHub(() => {
        if (!inboxNews()) return;
        stop();
        if (zone.scene) dotAt();
      });
      const stop = () => {
        stopNews();
        stopHub();
      };
      scene.events.once('shutdown', stop);
      zone.once('destroy', stop);
    }
  });
}

/** A tab's pulsing red dot (named, for tests: `club-dot`). */
function drawDot(scene: Phaser.Scene, x: number, y: number, name: string): void {
  const dot = scene.add.graphics().setPosition(x, y).setName(name);
  dot.fillStyle(UI.shadow).fillRect(-2, -1, 5, 5);
  dot.fillStyle(UI.danger).fillRect(-2, -2, 4, 4);
  dot.fillStyle(0xffffff, 0.7).fillRect(-1, -2, 1, 1);
  scene.tweens.add({
    targets: dot,
    scale: { from: 1, to: 1.35 },
    duration: 500,
    yoyo: true,
    repeat: -1,
    ease: 'Sine.easeInOut',
  });
}

/** A 9 x 9 gear. */
/** A small envelope (11 x 8) at (x, y), with a red dot at its corner when `news`. */
function drawEnvelope(g: Phaser.GameObjects.Graphics, x: number, y: number, news: boolean): void {
  g.fillStyle(UI.text).fillRect(x, y, 11, 8);
  g.fillStyle(UI.panel).fillRect(x + 1, y + 1, 9, 6);
  // The flap: a V from the top corners to the middle.
  for (let i = 0; i < 5; i++) {
    g.fillStyle(UI.text).fillRect(x + 1 + i, y + 1 + i, 1, 1);
    g.fillStyle(UI.text).fillRect(x + 9 - i, y + 1 + i, 1, 1);
  }
  if (news) g.fillStyle(UI.danger).fillRect(x + 9, y - 2, 4, 4);
}

function drawGear(g: Phaser.GameObjects.Graphics, x: number, y: number): void {
  const rows = [
    '...#.#...',
    '.#######.',
    '.##...##.',
    '##.....##',
    '.#.....#.',
    '##.....##',
    '.##...##.',
    '.#######.',
    '...#.#...',
  ];
  g.fillStyle(UI.dim);
  rows.forEach((row, ry) =>
    [...row].forEach((c, rx) => {
      if (c === '#') g.fillRect(x + rx, y + ry, 1, 1);
    }),
  );
}

/** Each tab's 11 x 11 icon, drawn in one color. */
const ICONS: Record<NavTab, string[]> = {
  // A little house with a lawn in front.
  lawns: [
    '.....#.....',
    '....###....',
    '...#####...',
    '..#######..',
    '.#########.',
    '..#.#.#.#..',
    '..#######..',
    '..###.###..',
    '..###.###..',
    '...........',
    '###########',
  ],
  // A calendar page.
  daily: [
    '..#.....#..',
    '###########',
    '###########',
    '#.........#',
    '#.##.##.#.#',
    '#.........#',
    '#.##.##.#.#',
    '#.........#',
    '#.##.#....#',
    '#.........#',
    '###########',
  ],
  // An infinity sign: endless (owner, 2026-10-30: the looping arrow did not read).
  runs: [
    '...........',
    '...........',
    '...........',
    '.##.....##.',
    '#..#...#..#',
    '#...#.#...#',
    '#....#....#',
    '#...#.#...#',
    '#..#...#..#',
    '.##.....##.',
    '...........',
  ],
  // An open-end wrench, its jaw up (owner, 2026-10-30: the ring-ended one did not read).
  garage: [
    '......#...#',
    '......#...#',
    '......##.##',
    '.....####..',
    '....###....',
    '...###.....',
    '..###......',
    '.###.......',
    '###........',
    '##.........',
    '...........',
  ],
  // A cup on a stand.
  club: [
    '#.#######.#',
    '###.....###',
    '#.#.....#.#',
    '#.#.....#.#',
    '.##.....##.',
    '...#...#...',
    '....###....',
    '.....#.....',
    '.....#.....',
    '...#####...',
    '...#####...',
  ],
};

function drawTabIcon(
  g: Phaser.GameObjects.Graphics,
  tab: NavTab,
  x: number,
  y: number,
  color: number,
): void {
  const rows = ICONS[tab];
  g.fillStyle(UI.shadow);
  rows.forEach((row, ry) =>
    [...row].forEach((c, rx) => {
      if (c === '#') g.fillRect(x + rx + 1, y + ry + 1, 1, 1);
    }),
  );
  g.fillStyle(color);
  rows.forEach((row, ry) =>
    [...row].forEach((c, rx) => {
      if (c === '#') g.fillRect(x + rx, y + ry, 1, 1);
    }),
  );
}
